import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Megaphone, Plus, Trash2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export function AnnouncementBanner() {
  const [announcements, setAnnouncements] = useState([]);
  const [dismissed, setDismissed] = useState([]);

  useEffect(() => {
    const fetchAnns = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        const res = await axios.get(`${API}/announcements`, { headers: { Authorization: `Bearer ${token}` } });
        setAnnouncements(res.data);
      } catch (err) { console.error("./pages/Announcements.jsx:", err); }
    };
    fetchAnns();
  }, []);

  const visible = announcements.filter(a => !dismissed.includes(a.id));
  if (visible.length === 0) return null;

  return (
    <div className="space-y-2 mb-6">
      {visible.map(ann => (
        <div key={ann.id} data-testid={`announcement-${ann.id}`} className="bg-gradient-to-r from-blue-50 to-cyan-50 border border-blue-200 rounded p-4 flex items-start gap-3">
          <Megaphone size={18} className="text-blue-600 mt-0.5 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold text-blue-900">{ann.title}</p>
            <p className="text-sm text-slate-600">{ann.message}</p>
            <p className="text-xs text-slate-400 mt-1">— {ann.created_by_name}, {new Date(ann.created_at).toLocaleDateString()}</p>
          </div>
          <button onClick={() => setDismissed(prev => [...prev, ann.id])} className="text-slate-400 hover:text-slate-600">
            <X size={16} />
          </button>
        </div>
      ))}
    </div>
  );
}

export default function Announcements() {
  const [announcements, setAnnouncements] = useState([]);
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [user, setUser] = useState(null);

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) setUser(JSON.parse(userData));
  }, []);

  const fetchAnns = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/announcements`, { headers: { Authorization: `Bearer ${token}` } });
      setAnnouncements(res.data);
    } catch { toast.error('Failed to fetch announcements'); }
  };

  useEffect(() => { fetchAnns(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/announcements`, { title, message }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Announcement posted');
      setTitle('');
      setMessage('');
      fetchAnns();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to post announcement'); }
  };

  const handleDelete = async (id) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/announcements/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Announcement removed');
      fetchAnns();
    } catch { toast.error('Failed to remove announcement'); }
  };

  const isAdmin = user?.role === 'admin';

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-blue-900 mb-2" data-testid="announcements-title" style={{ fontFamily: 'Manrope, sans-serif' }}>Announcements</h1>
        <p className="text-slate-600">Post notices visible to all team members</p>
      </div>

      {isAdmin && (
        <form onSubmit={handleSubmit} className="bg-white border border-blue-100 rounded p-6 mb-6 shadow-sm" data-testid="announcement-form">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Title</Label>
              <Input data-testid="ann-title-input" value={title} onChange={(e) => setTitle(e.target.value)} required className="mt-2 rounded border-blue-200" placeholder="e.g., New Client Onboarded" />
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Message</Label>
              <Input data-testid="ann-message-input" value={message} onChange={(e) => setMessage(e.target.value)} required className="mt-2 rounded border-blue-200" placeholder="Details for the team..." />
            </div>
          </div>
          <Button data-testid="post-announcement-button" type="submit" className="mt-4 bg-blue-600 hover:bg-blue-700 text-white rounded font-bold">
            <Plus size={16} className="mr-2" /> Post Announcement
          </Button>
        </form>
      )}

      <div className="space-y-3">
        {announcements.length === 0 ? (
          <div className="bg-white border border-blue-100 rounded p-12 text-center">
            <Megaphone size={48} className="mx-auto mb-4 text-slate-300" />
            <p className="text-slate-500" data-testid="no-announcements">No announcements yet.</p>
          </div>
        ) : (
          announcements.map(ann => (
            <div key={ann.id} data-testid={`ann-row-${ann.id}`} className="bg-white border border-blue-100 rounded p-5 flex items-start gap-4 hover:shadow-sm transition-shadow">
              <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center flex-shrink-0">
                <Megaphone size={18} className="text-blue-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-blue-900">{ann.title}</h3>
                <p className="text-sm text-slate-600 mt-1">{ann.message}</p>
                <p className="text-xs text-slate-400 mt-2">Posted by {ann.created_by_name} on {new Date(ann.created_at).toLocaleDateString()}</p>
              </div>
              {isAdmin && (
                <Button size="sm" variant="ghost" data-testid={`delete-ann-${ann.id}`} onClick={() => handleDelete(ann.id)} className="text-red-600 hover:text-red-900 hover:bg-red-50">
                  <Trash2 size={14} />
                </Button>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
